import { SkeletonCircle, SkeletonText } from '@chakra-ui/react';
import { UiStack, uiStyles } from '@/lib/ui';
import RegisterLayout from '@/registration/component/Register/Layout';
import { useEventQuery } from '@/registration/hook/useEventQuery';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { type FC, type PropsWithChildren } from 'react';
import BaseMessageBarError from '@/base/MessageBar/Error';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import { LocaleProvider } from '@/registration/component/Locale/LocaleProvider';

interface PropsWithChildrenForMode extends PropsWithChildren {
  skipCloseMode?: boolean
}

const ModeGuard: FC<PropsWithChildrenForMode> = ({ children, skipCloseMode = false }) => {
  const { eventId } = useRegisterRoute();
  const eventQuery = useEventQuery(eventId);
  const eventSetting = useEventSettingsQuery(eventId);

  /**
   * @todo We don't recommend to write inline functions. It's hard to read and change (e.g. if we need to add more lines in the function body).
   */

  if ((eventQuery?.isLoading || eventSetting?.isLoading)) { // loading registration
    return (
      <RegisterLayout enableHeader={false}>
        <SkeletonCircle size='16' />
        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
      </RegisterLayout>
    );
  }

  if (eventQuery?.data?.registrationMode === 'Building') {
    return (
      <RegisterLayout enableHeader={false}>
        <UiStack alignItems="stretch">
          <BaseMessageBarError borderRadius={uiStyles.borderRadius}>
            {`${eventQuery?.data?.name}'s registration has not opened yet.`}
          </BaseMessageBarError>
          {!!eventSetting?.data?.pageMetadata?.building?.message && (
            <BaseMessageBarError borderRadius={uiStyles.borderRadius}>
              {eventSetting?.data?.pageMetadata?.building?.message}
            </BaseMessageBarError>
          )}
        </UiStack>
      </RegisterLayout>
    );
  }

  if ((eventQuery?.data?.registrationMode === 'Closed' && !skipCloseMode)) {
    return (
      <RegisterLayout enableHeader={false}>
        <UiStack alignItems="stretch" spacing={4}>
          <BaseMessageBarInfo borderRadius={uiStyles.borderRadius} bgColor={'green.50'}>
            {`${eventQuery?.data?.name}'s registration has now completed.`}
          </BaseMessageBarInfo>
          {!!eventSetting?.data?.pageMetadata?.closed?.message && (
            <BaseMessageBarInfo borderRadius={uiStyles.borderRadius}>
              {eventSetting?.data?.pageMetadata?.closed?.message}
            </BaseMessageBarInfo>
          )}
        </UiStack>
      </RegisterLayout>
    );
  }

  return (
    <LocaleProvider eventId={eventId}>
      {children}
    </LocaleProvider>
  )
};

export default ModeGuard;

// /**
//  * @todo This is NOT a good component to include RouteGuard. Even if we want to do this, we should allow pages that don't need registration auth to work without it.
//  */
// const RegisterContainer: FC<PropsWithChildren> = ({children}) => (
//     <RouteGuard>
//       {children}
//     </RouteGuard>
// );
//
// export default RegisterContainer;
